/**
 * ACRES Admin Dashboard — tabbed shell
 * Hosts: Templates, Moderation Queue, Lucky Draws, Achievements, Banner, Analytics
 */
import React, { useState, useMemo } from 'react';
import { AcresTaskTemplates } from './acres/AcresTaskTemplates';
import { AcresModeration } from './acres/AcresModeration';
import { AcresLuckyDraws } from './acres/AcresLuckyDraws';
import { AcresAchievementsAdmin } from './acres/AcresAchievementsAdmin';
import { AcresBannerAdmin } from './acres/AcresBannerAdmin';
import { AcresAnalyticsAdmin } from './acres/AcresAnalyticsAdmin';

interface AcresDashboardProps {
  currentUser?: any;
  initialTab?: AcresTabId;
}

type AcresTabId = 'overview' | 'templates' | 'moderation' | 'draws' | 'achievements' | 'banner';

const TABS: Array<{ id: AcresTabId; label: string; icon: string; permission: string }> = [
  { id: 'overview',     label: 'Overview',     icon: '📊', permission: 'view_acres_analytics' },
  { id: 'moderation',   label: 'Moderation',   icon: '🛡️', permission: 'moderate_acres_tasks' },
  { id: 'templates',    label: 'Tasks',        icon: '📝', permission: 'manage_acres_tasks' },
  { id: 'draws',        label: 'Lucky Draws',  icon: '🎰', permission: 'manage_acres_draws' },
  { id: 'achievements', label: 'Achievements', icon: '🏆', permission: 'manage_acres_achievements' },
  { id: 'banner',       label: 'Promo Banner', icon: '📣', permission: 'manage_acres_banner' }
];

export const AcresDashboard: React.FC<AcresDashboardProps> = ({ currentUser, initialTab }) => {
  const [activeTab, setActiveTab] = useState<AcresTabId>(initialTab || 'overview');

  const userPerms: string[] = useMemo(() => {
    const role = currentUser?.adminUser?.role || currentUser?.role;
    if (role === 'super_admin' || role === 'admin') return ['*'];
    return Array.isArray(currentUser?.adminPermissions) ? currentUser.adminPermissions : [];
  }, [currentUser]);

  const canSee = (perm: string) => userPerms.includes('*') || userPerms.includes(perm);
  const visibleTabs = TABS.filter(t => canSee(t.permission));

  return (
    <div style={{ color: '#FFFFFF' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#F59E0B', margin: 0 }}>
          ACRES — Aureus Community Reward & Engagement
        </h2>
        <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>
          Manage tasks, moderate submissions, run lucky draws, configure achievements and the promo banner. All financial actions go through the immutable points ledger.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20, borderBottom: '1px solid #1E1E1E', paddingBottom: 12 }}>
        {visibleTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid ' + (activeTab === t.id ? '#F59E0B' : '#374151'),
              background: activeTab === t.id ? 'rgba(245,158,11,0.12)' : 'transparent',
              color: activeTab === t.id ? '#F59E0B' : '#E5E7EB',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <AcresAnalyticsAdmin currentUser={currentUser} />}
      {activeTab === 'moderation' && <AcresModeration currentUser={currentUser} />}
      {activeTab === 'templates' && <AcresTaskTemplates currentUser={currentUser} />}
      {activeTab === 'draws' && <AcresLuckyDraws currentUser={currentUser} />}
      {activeTab === 'achievements' && <AcresAchievementsAdmin currentUser={currentUser} />}
      {activeTab === 'banner' && <AcresBannerAdmin currentUser={currentUser} />}

      {visibleTabs.length === 0 && (
        <div style={{ padding: 24, background: 'rgba(220,38,38,0.08)', border: '1px solid #DC2626', borderRadius: 8 }}>
          You do not have permission to access any ACRES management tools. Contact a super admin for access.
        </div>
      )}
    </div>
  );
};

export default AcresDashboard;
